import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { safeJsonParse } from '@dealstack/shared/lib/format/json';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import type {
	UseServerSentEventsArgs,
	UseServerSentEventsResult,
} from '@dealstack/frontend/shared/hooks/useServerSentEvents.types';

const isEventSourceSupported = (): boolean => typeof EventSource !== 'undefined';

export const useServerSentEvents = <TMessage>({
	path,
	enabled,
	onMessage,
	onResync,
	logLabel,
}: UseServerSentEventsArgs<TMessage>): UseServerSentEventsResult => {
	const [isConnected, setIsConnected] = useState(false);

	const onMessageRef = useRef(onMessage);
	const onResyncRef = useRef(onResync);
	useLayoutEffect(() => {
		onMessageRef.current = onMessage;
		onResyncRef.current = onResync;
	});

	useEffect(() => {
		if (!enabled || !isEventSourceSupported()) {
			setIsConnected(false);
			return;
		}

		const { apiUrl } = getGlobalConfig();
		// API_PATHS values carry a leading slash; older callers pass a bare path.
		const eventSource = new EventSource(`${apiUrl}/${path.replace(/^\//, '')}`, {
			withCredentials: true,
		});

		eventSource.onopen = () => {
			setIsConnected(true);
			logger.debug(`${logLabel} SSE connected; resyncing`);
			onResyncRef.current();
		};

		eventSource.onmessage = (event: MessageEvent<string>) => {
			setIsConnected(true);
			const message = safeJsonParse<TMessage>(event.data);
			if (message) {
				onMessageRef.current(message);
			}
		};

		eventSource.onerror = () => {
			setIsConnected(false);
			logger.warn(`${logLabel} SSE connection error; live updates paused`);
		};

		return () => {
			eventSource.close();
			setIsConnected(false);
		};
	}, [enabled, path, logLabel]);

	return { isConnected, isSupported: isEventSourceSupported() };
};
